import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {proxyFetchNodeInventory as api, ForSuccess, ResourceState, UseTypedSelector} from 'utils/type-safe-api';

export namespace ScheduledSearchProps {
  type Empty<T> = {[K in keyof T]: T[K] | undefined};
  export type Campaign = {campaignId: string | number};
  export type Scrapeology = {scrapeologyDjangoId: number; scrapeologyNameId: string};
  export type FilterList = {filterListId: number};
  export type Summary = {summaryId: number};

  export type EmptyCampaign = Empty<Campaign>;
  export type EmptyScrapeology = Empty<Scrapeology>;
  export type EmptyFilterList = Empty<FilterList>;
  export type EmptySummary = Empty<Summary>;
}

export type UseScheduledSlice = UseTypedSelector<
  [
    [
      ScheduledSearchProps.Campaign,
      {} & Fetch<'projectList', 'getProjectList'> &
        Fetch<'itemConfigList', 'getItemConfigList'> &
        Fetch<'summaryList', 'getSummaryList'> &
        Fetch<'summaryDatedRecords', 'getSummaryByDate'> &
        Fetch<'summaryDeployments', 'getDeployEnrollments'> &
        Fetch<'filterListAll', 'getFilterListAll'>,
    ],
    [
      ScheduledSearchProps.Scrapeology,
      {} & Fetch<'filterList', 'getFilterList'> &
        Fetch<'project', 'getProject'> &
        Fetch<'projectDays', 'getProjectDays'>,
    ],
    [ScheduledSearchProps.FilterList, {} & Fetch<'filterDayList', 'getFilterDayList'>],
  ]
>[2];

type Fetch<N extends string, E extends keyof typeof endpoints> = {
  [K in N]: {
    success: ForSuccess<(typeof endpoints)[E]>;
    state: ResourceState;
  };
};
type InitialState = Fetch<'projectList', 'getProjectList'> &
  Fetch<'project', 'getProject'> &
  Fetch<'projectDays', 'getProjectDays'> &
  Fetch<'filterList', 'getFilterList'> &
  Fetch<'filterListAll', 'getFilterListAll'> &
  Fetch<'filterDayList', 'getFilterDayList'> &
  Fetch<'filterTest', 'testFilter'> &
  Fetch<'summaryList', 'getSummaryList'> &
  Fetch<'summaryDatedRecords', 'getSummaryByDate'> &
  Fetch<'summaryDeployments', 'getDeployEnrollments'> &
  Fetch<'itemConfigList', 'getItemConfigList'> &
  Fetch<'itemList', 'getItemList'> & {
    /*
     * ### Open tab
     * + `.value` - which is open currently
     * + else - choices of tabs available
     */
    tabs: {
      value: {
        root: number;
        project: number;
      };
      root: {
        project: number;
        summary: number;
      };
      project: {
        setup: number;
        overview: number;
        filter: number;
      };
    };
    /*
     * ### Open modal
     * + `.value` - which is open currently
     * + `.opts` - choices of modals available
     * `if (modal.value < 0)` than no modal is showing.
     */
    modal: {
      value: number;
      opts: {
        projectItemList: number;
        projectTestFilter: number;
        projectCreate: number;
        reportCreate: number;
        projectSaveFilterLoading: number;
        reportCreateLoading: number;
        summaryCreateDelivery: number;
      };
    };
    /** ### In-app selected data. */
    state: {
      /** ### Inventory Project's database ID. */
      projectScrapeologyDjangoId: number | undefined;
      /** ### Inventory Project Filter's database ID. */
      projectFilterId: number | undefined;
      /** ### Inventory Project's Scrapeology ID. */
      projectScrapeologyNameId: string | undefined;
      /** ### Item List database ID */
      projectItemConfigId: number | undefined;
      /** ### User Date to sample Filter. */
      projectTestFilterDate: string | undefined;
      /** ### A Report over many Projects */
      summaryId: number | undefined;
      calendarMonthIndex: number | undefined;
      calendarFullYear: number | undefined;
      projectFilterListId: number | undefined;
      /** ### Selected Date to view Summary Filters for. */
      summaryDateIdx: number;
    };
  };
const endpoints = {
  getProjectList: createAsyncThunk(`scheduledSearch/getProjectList`, api.scrapeBriefsCached),
  createProjectList: createAsyncThunk(`scheduledSearch/createProjectList`, api.scrapeCreate),
  getProject: createAsyncThunk(`scheduledSearch/getProject`, api.scrapeInfoCached),
  getProjectDays: createAsyncThunk(`scheduledSearch/getProjectDays`, api.scrapeDaysListCached),
  execProjectDay: createAsyncThunk(`scheduledSearch/execProjectDay`, api.reportAllProductFulfillment),

  getFilterList: createAsyncThunk(`scheduledSearch/getFilterList`, api.sqlFilterList),
  getFilterListAll: createAsyncThunk(`scheduledSearch/getFilterListAll`, api.sqlFilterListMany),
  createFilter: createAsyncThunk(`scheduledSearch/createFilter`, api.sqlFilterCreate),
  testFilter: createAsyncThunk(`scheduledSearch/testFilter`, api.sqlFilterTest),
  execFilterDayList: createAsyncThunk(`scheduledSearch/execFilterDayList`, api.sqlFilterDemand),
  getFilterDayList: createAsyncThunk(`scheduledSearch/getFilterDayList`, api.sqlFilterDayList),

  getSummaryList: createAsyncThunk(`scheduledSearch/getSummaryList`, api.sqlSummaryList),
  getSummaryByDate: createAsyncThunk(`scheduledSearch/getSummaryByDate`, api.sqlSummaryByDate),
  createSummaryList: createAsyncThunk(`scheduledSearch/createSummaryList`, api.sqlSummaryCreate),

  getItemConfigList: createAsyncThunk(`scheduledSearch/getItemConfigList`, api.sqlItemListConfigs),
  getItemList: createAsyncThunk(`scheduledSearch/getItemList`, api.sqlItemList),

  getDeployEnrollments: createAsyncThunk(`scheduledSearch/getDeployEnrollments`, api.sqlDeploymentEnrollments),
  createDeployS3: createAsyncThunk(`scheduledSearch/createDeployS3`, api.s3DeploymentCreate),
  createDeployTD: createAsyncThunk(`scheduledSearch/createDeployTD`, api.tdDeploymentCreate),
  execDeployS3: createAsyncThunk(`scheduledSearch/execDeployS3`, api.sqlDeployImmediateS3),
  execDeployTD: createAsyncThunk(`scheduledSearch/execDeployTD`, api.sqlDeployImmediateTd),
};
const initialState: InitialState = {
  /** ### A Scrapeology project. */
  projectList: {
    success: [],
    state: 'start',
  },
  /** ### Details for a Scrapeology project. */
  project: {
    success: null,
    state: 'start',
  },
  /** ### Days ran for a Scrapeology project. */
  projectDays: {
    success: null,
    state: 'start',
  },
  /** ### A Scrapeology project's ItemConfig that are applied. */
  filterList: {
    success: [],
    state: 'start',
  },
  /** ### A Campaign's ItemConfig that are applied. */
  filterListAll: {
    success: [],
    state: 'start',
  },
  /** ### A saved ItemConfig onto a Scrapeology project. */
  filterDayList: {
    success: [],
    state: 'start',
  },
  /** ### Sample results after executing an ItemConfig on a Scrapeology project's. */
  filterTest: {
    success: [],
    state: 'start',
  },
  /** ### A Campaign's combination of filters into a data-per-day summary. */
  summaryList: {
    success: [],
    state: 'start',
  },
  /** ### A Summary's data-per-day-per-filter results. */
  summaryDatedRecords: {
    success: [],
    state: 'start',
  },
  /** ### A Summary's enrolled Deployments. */
  summaryDeployments: {
    success: null,
    state: 'start',
  },
  /** ### A Campaign's item lists & configs created in Digital Shelf. */
  itemConfigList: {
    success: [],
    state: 'start',
  },
  /** ### The individual items from the `itemConfigList`. */
  itemList: {
    success: {},
    state: 'start',
  },
  tabs: {
    value: {
      root: 0,
      project: 1,
    },
    root: {
      project: 0,
      summary: 1,
    },
    project: {
      setup: 0,
      overview: 1,
      filter: 2,
    },
  },
  modal: {
    value: -1,
    opts: {
      projectItemList: 1,
      projectTestFilter: 2,
      projectCreate: 3,
      reportCreate: 4,
      projectSaveFilterLoading: 5,
      reportCreateLoading: 6,
      summaryCreateDelivery: 7,
    },
  },
  state: {
    projectScrapeologyDjangoId: undefined,
    projectFilterId: undefined,
    projectScrapeologyNameId: undefined,
    projectItemConfigId: undefined,
    projectTestFilterDate: undefined,
    projectFilterListId: undefined,
    summaryId: undefined,
    calendarFullYear: undefined,
    calendarMonthIndex: undefined,
    summaryDateIdx: 0,
  },
};
/** Case-builder for `extraReducers`'s `addCase` function. Returns a tuple. */
const CaseFor = {
  error: <T extends {rejected: unknown}, K extends keyof typeof initialState>(
    endpoint: T,
    stateProperty: K,
  ): [T['rejected'], (a: any) => void] => [
    endpoint.rejected,
    state => {
      state[stateProperty].state = 'error';
    },
  ],
  loading: <T extends {pending: unknown}, K extends keyof typeof initialState>(
    endpoint: T,
    stateProperty: K,
  ): [T['pending'], (a: any) => void] => [
    endpoint.pending,
    state => {
      state[stateProperty].state = 'loading';
    },
  ],
  success: <T extends {fulfilled: unknown}, K extends keyof typeof initialState>(
    endpoint: T,
    stateProperty: K,
  ): [T['fulfilled'], (a: any, b: any) => void] => [
    endpoint.fulfilled,
    (state, {payload}) => {
      state[stateProperty].success = payload;
      state[stateProperty].state = 'success';
    },
  ],
};

const slice = createSlice({
  name: 'scheduledSearch',
  initialState,
  reducers: {
    tabUpdate(store, {payload}: PayloadAction<{tabId: string; value: number}>) {
      store.tabs.value[payload.tabId] = +payload.value;
    },
    modalUpdateDeprecated(store, {payload}: PayloadAction<{id: number} | undefined>) {
      store.modal.value = payload ? payload.id : -1;
    },
    modalUpdate(store, {payload}: PayloadAction<keyof InitialState['modal']['opts'] | undefined>) {
      store.modal.value = payload ? store.modal.opts[payload] : -1;
    },
    projectSelect(store, {payload}: PayloadAction<ForSuccess<(typeof endpoints)['getProjectList']>[number] | null>) {
      if (payload) {
        store.state.projectScrapeologyDjangoId = payload.scrapeologyDjangoId;
        store.state.projectScrapeologyNameId = payload.projectId;
      } else {
        store.state.projectScrapeologyDjangoId = -1;
        store.state.projectScrapeologyNameId = '';
      }
    },
    projectItemConfigIdSet(
      store,
      {payload}: PayloadAction<ForSuccess<(typeof endpoints)['getItemConfigList']>[number] | null>,
    ) {
      if (payload) store.state.projectItemConfigId = Number(payload.id);
      else store.state.projectItemConfigId = -1;
    },
    projectFilterSelect(store, {payload}: PayloadAction<{itemConfigId: any}>) {
      store.state.projectFilterListId = payload.itemConfigId;
    },
    projectTestFilterDate(store, {payload}: PayloadAction<{date: string | undefined}>) {
      store.state.projectTestFilterDate = payload.date || '';
    },
    summarySelect(store, {payload}: PayloadAction<ForSuccess<(typeof endpoints)['getSummaryList']>[number]>) {
      store.state.summaryId = Number(payload.id);
    },
    summaryDateSelect(store, {payload}: PayloadAction<number>) {
      store.state.summaryDateIdx = payload;
    },
    calMonthNext(store) {
      const monthIdx = store.state.calendarMonthIndex ?? new Date().getUTCMonth();
      const yearFull = store.state.calendarFullYear ?? new Date().getUTCFullYear();
      const date = new Date(`${`${monthIdx + 1}-`.padStart(3, '0')}${`01-${`${yearFull}`.slice(-2)}`}`);
      date.setUTCMonth(date.getUTCMonth() + 1);
      store.state.calendarMonthIndex = date.getUTCMonth();
      store.state.calendarFullYear = date.getUTCFullYear();
    },
    calMonthPrev(store) {
      const monthIdx = store.state.calendarMonthIndex ?? new Date().getUTCMonth();
      const yearFull = store.state.calendarFullYear ?? new Date().getUTCFullYear();
      const date = new Date(`${`${monthIdx + 1}-`.padStart(3, '0')}${`01-${`${yearFull}`.slice(-2)}`}`);
      date.setUTCMonth(date.getUTCMonth() - 1);
      store.state.calendarMonthIndex = date.getUTCMonth();
      store.state.calendarFullYear = date.getUTCFullYear();
    },
    calInit(store, {payload}: PayloadAction<{date: number}>) {
      const date = new Date(payload.date);
      store.state.calendarMonthIndex = date.getUTCMonth();
      store.state.calendarFullYear = date.getUTCFullYear();
    },
  },
  extraReducers: builder =>
    builder
      // Get Project List
      .addCase(...CaseFor.success(endpoints.getProjectList, 'projectList'))
      .addCase(...CaseFor.loading(endpoints.getProjectList, 'projectList'))
      .addCase(...CaseFor.error(endpoints.getProjectList, 'projectList'))
      // Get Project Days
      .addCase(...CaseFor.success(endpoints.getProjectDays, 'projectDays'))
      .addCase(...CaseFor.loading(endpoints.getProjectDays, 'projectDays'))
      .addCase(...CaseFor.error(endpoints.getProjectDays, 'projectDays'))
      // Get Project
      .addCase(...CaseFor.success(endpoints.getProject, 'project'))
      .addCase(...CaseFor.loading(endpoints.getProject, 'project'))
      .addCase(...CaseFor.error(endpoints.getProject, 'project'))
      // Get Filter List
      .addCase(...CaseFor.success(endpoints.getFilterList, 'filterList'))
      .addCase(...CaseFor.loading(endpoints.getFilterList, 'filterList'))
      .addCase(...CaseFor.error(endpoints.getFilterList, 'filterList'))
      // Get Filter List All
      .addCase(...CaseFor.success(endpoints.getFilterListAll, 'filterListAll'))
      .addCase(...CaseFor.loading(endpoints.getFilterListAll, 'filterListAll'))
      .addCase(...CaseFor.error(endpoints.getFilterListAll, 'filterListAll'))
      // Test Filter
      .addCase(...CaseFor.success(endpoints.testFilter, 'filterTest'))
      .addCase(...CaseFor.loading(endpoints.testFilter, 'filterTest'))
      .addCase(...CaseFor.error(endpoints.testFilter, 'filterTest'))
      // Get Filter Day List
      .addCase(...CaseFor.success(endpoints.getFilterDayList, 'filterDayList'))
      .addCase(...CaseFor.loading(endpoints.getFilterDayList, 'filterDayList'))
      .addCase(...CaseFor.error(endpoints.getFilterDayList, 'filterDayList'))
      // Get Summary List
      .addCase(...CaseFor.success(endpoints.getSummaryList, 'summaryList'))
      .addCase(...CaseFor.loading(endpoints.getSummaryList, 'summaryList'))
      .addCase(...CaseFor.error(endpoints.getSummaryList, 'summaryList'))
      // Get Summary Deployments
      .addCase(...CaseFor.success(endpoints.getDeployEnrollments, 'summaryDeployments'))
      .addCase(...CaseFor.loading(endpoints.getDeployEnrollments, 'summaryDeployments'))
      .addCase(...CaseFor.error(endpoints.getDeployEnrollments, 'summaryDeployments'))
      // Get Summary List
      .addCase(...CaseFor.success(endpoints.getSummaryByDate, 'summaryDatedRecords'))
      .addCase(...CaseFor.loading(endpoints.getSummaryByDate, 'summaryDatedRecords'))
      .addCase(...CaseFor.error(endpoints.getSummaryByDate, 'summaryDatedRecords'))
      // Get Item Config List
      .addCase(...CaseFor.success(endpoints.getItemConfigList, 'itemConfigList'))
      .addCase(...CaseFor.loading(endpoints.getItemConfigList, 'itemConfigList'))
      .addCase(...CaseFor.error(endpoints.getItemConfigList, 'itemConfigList'))
      // Get Item List
      .addCase(...CaseFor.success(endpoints.getItemList, 'itemList'))
      .addCase(...CaseFor.loading(endpoints.getItemList, 'itemList'))
      .addCase(...CaseFor.error(endpoints.getItemList, 'itemList')),
});

export const scheduledSearchSlice = slice;
export const scheduledSearchEndpoints = endpoints;
export const scheduledSearchActions = slice.actions;
// export const ScheduledSearchProps = Props;

export type ScheduledSearchState = typeof initialState;
export type ScheduledSearchSlice = {
  [slice.name]: ReturnType<(typeof scheduledSearchSlice)['reducer']>;
};
